import React, {Component, useEffect, useState} from "react";
import {Button, Card, ListGroup} from "react-bootstrap";
import {getAuth, onAuthStateChanged} from "@firebase/auth";
import { NewsItem, Session, User } from "../types/Types";
import {collection, getDocs, limit, orderBy, query, where} from "@firebase/firestore";
import {db, functions} from "../../firebase/FirebaseProvider";
import NewsItemRow from "./NewsItemRow";
import HubNav from "./HubNav";
import {NOTIFICATION_LIMIT, logDataLoaded} from "../util/Util";
import PageTracking from "../auth/PageTracking";
import {useNavigate} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHeart, faCalendar} from "@fortawesome/free-solid-svg-icons";
import {V2GlassChip, V2GlassPanel} from "../shared/v2/V2Glass";
import V2CommentRow from "../shared/v2/V2CommentRow";

interface Props {
    usersMap: Map<string, User>;
    sessionsMap: Map<string, Session>;
    currentUser: User | undefined;
    likesMap: Map<string, boolean>;
}

function Comments(props: Props) {
    const navigate = useNavigate();
    const isUiV2 = !!props.currentUser?.enableUiV2;
    const [newsItems, setNewsItems] = useState<Array<NewsItem> | undefined>(undefined);
    const [sortBy, setSortBy] = useState<'date' | 'likes'>('date');

    const updateCommentLikeCount = (commentId: string, adjustment: number) => {
        setNewsItems(prevNewsItems => {
            if (!prevNewsItems) return prevNewsItems;
            return prevNewsItems.map(item => {
                if (item.id === commentId) {
                    return {
                        ...item,
                        likeCount: (item.likeCount || 0) + adjustment
                    };
                }
                return item;
            });
        });
    };

    useEffect(() => {
        (async () => {
            const auth = getAuth();
            onAuthStateChanged(auth, async (currentUser) => {
                if (!currentUser) {
                    navigate('/');
                    return;
                }
            });
        })();
    }, []);

    useEffect(() => {
        (async () => {
            await loadAllData();
        })();
    }, [sortBy]);

    const loadAllData = async() => {
        let newsItems: Array<NewsItem> = [];
        const orderByField = sortBy === 'likes' ? 'likeCount' : 'lastModified';
        const newsOnceCollection = query(collection(db, 'comments'),
            where('hidden', '==', false),
            orderBy(orderByField, 'desc'),
            ...(sortBy === 'likes' ? [orderBy('lastModified', 'desc')] : []),
            limit(NOTIFICATION_LIMIT));
        const newsOnceCollectionSnapshot = await getDocs(newsOnceCollection);
        newsOnceCollectionSnapshot.forEach((doc) => {
            let tempNewsItem = doc.data() as NewsItem;
            tempNewsItem.id = doc.id;
            // Ensure likeCount exists, default to 0 if undefined
            if (tempNewsItem.likeCount === undefined) {
                tempNewsItem.likeCount = 0;
            }
            newsItems.push(tempNewsItem);
        });
        logDataLoaded('comments', newsItems.length);
        setNewsItems(newsItems);
    };

    return (
        <>
            <PageTracking />
            <HubNav currentUser={props.currentUser} />
            {isUiV2 ? <V2GlassPanel className="hub-v2-comments-panel v2-glass-list-panel">
                <div className="hub-v2-comments-header">
                    <V2GlassChip className="hub-v2-comments-count">
                        Comments: <b>{newsItems?.length || 0}</b>
                    </V2GlassChip>
                    <div className="hub-v2-comments-sort" aria-label="Sort comments">
                        <button
                            className={`hub-v2-sort-button${sortBy === 'date' ? " active" : ""}`}
                            disabled={sortBy === 'date'}
                            type="button"
                            aria-pressed={sortBy === 'date'}
                            onClick={() => setSortBy('date')}
                        >
                            <FontAwesomeIcon icon={faCalendar} />
                            <span>Recent</span>
                        </button>
                        <button
                            className={`hub-v2-sort-button${sortBy === 'likes' ? " active" : ""}`}
                            disabled={sortBy === 'likes'}
                            type="button"
                            aria-pressed={sortBy === 'likes'}
                            onClick={() => setSortBy('likes')}
                        >
                            <FontAwesomeIcon icon={faHeart} />
                            <span>Liked</span>
                        </button>
                    </div>
                </div>
                {!newsItems && <div className="hub-v2-empty-list">Loading...</div>}
                {newsItems && newsItems.length === 0 && <div className="hub-v2-empty-list">No comments yet</div>}
                {newsItems && newsItems.map((newsItem: NewsItem) => (
                    <V2CommentRow
                        key={newsItem.id}
                        user={props.usersMap.get(newsItem.userId)}
                        comment={newsItem}
                        session={props.sessionsMap.get(newsItem.userId)}
                        currentUser={props.currentUser}
                        likesMap={props.likesMap}
                        onLikeCountChange={updateCommentLikeCount}
                    />
                ))}
            </V2GlassPanel> : <Card className={"mb-3"}>
                <Card.Header className="header-padding">
                    <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                        <Button 
                            className={"btn-sm mr-2 cursor-pointer"} 
                            disabled={sortBy === 'date'}
                            onClick={() => setSortBy('date')}
                        >
                            <FontAwesomeIcon icon={faCalendar} />
                        </Button>
                        <Button 
                            className={"btn-sm cursor-pointer"} 
                            disabled={sortBy === 'likes'}
                            onClick={() => setSortBy('likes')}
                        >
                            <FontAwesomeIcon icon={faHeart} />
                        </Button>
                    </div>
                </Card.Header>
                <ListGroup variant="flush">
                    {!newsItems && <div className={'loading'}>
                        Loading...
                    </div>}
                    {(newsItems && newsItems.length == 0) &&<div className={'loading'}>
                        No comments yet
                    </div>}
                    {newsItems && newsItems.map((newsItem: NewsItem, index: number) => (
                        <NewsItemRow 
                            key={newsItem.id}
                            user={props.usersMap.get(newsItem.userId)} 
                            newsItem={newsItem} 
                            session={props.sessionsMap.get(newsItem.userId)}
                            currentUser={props.currentUser}
                            likesMap={props.likesMap}
                            onLikeCountChange={updateCommentLikeCount}
                        />
                    ))}
                </ListGroup>
            </Card>}
        </>
    )
}

export default Comments;
